import { useEffect, useRef, useState } from "react";

interface LottiePlayerProps {
  src: string;
  fallbackSrcs?: string[];
  /** Shown at large size if all URLs fail (e.g. named emoji) */
  fallbackEmoji?: string;
  className?: string;
  "aria-label"?: string;
}

export function LottiePlayer({
  src,
  fallbackSrcs = [],
  fallbackEmoji = "📌",
  className,
  "aria-label": ariaLabel,
}: LottiePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let destroyed = false;
    let anim: { destroy: () => void } | null = null;
    const urls = [src, ...fallbackSrcs].filter(Boolean);

    async function tryLoad(urlsToTry: string[]): Promise<void> {
      const lottie = await import("lottie-web");
      let lastErr: unknown;
      for (const url of urlsToTry) {
        try {
          const res = await fetch(url, { mode: "cors" });
          if (!res.ok) {
            lastErr = new Error(String(res.status));
            continue;
          }
          const data = await res.json();
          if (destroyed || !containerRef.current) return;
          anim = lottie.default.loadAnimation({
            container: containerRef.current,
            renderer: "svg",
            loop: true,
            autoplay: true,
            animationData: data,
          });
          return;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!destroyed) {
        console.warn("LottiePlayer: all URLs failed", lastErr);
        setError(true);
      }
    }

    setError(false);
    tryLoad(urls).catch(() => {
      if (!destroyed) setError(true);
    });

    return () => {
      destroyed = true;
      anim?.destroy();
    };
  }, [src, JSON.stringify(fallbackSrcs)]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center text-[4rem] leading-none ${className || ""}`}
        role="img"
        aria-label={ariaLabel}
      >
        {fallbackEmoji}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      role="img"
      aria-label={ariaLabel || "Animated illustration"}
    />
  );
}
